// 1b: A LINE THAT HAS NO PRICE YET, AND WHY IT MUST NOT REACH THE ENGINE.
//
// WHAT THIS IS FOR. The Add Materials picker now reads the REAL product
// master, and anixify.product carries no price -- deliberately, and
// permanently: every price in this system comes from the engine, computed
// from the six inputs on a quote line, and a price on the catalog would be a
// second place a number could come from. So a picked product arrives with no
// listPrice and no netPrice, and stays that way until someone types one.
//
// ⚠️ THE CORE INVARIANT OF THE WHOLE SLICE: SUCH A LINE MUST NEVER BE SENT TO
// price_lines. Not "should not" -- must not. There are exactly two ways it
// could be, and BOTH are worse than not pricing it:
//
//   1. AS A ZERO. `(listPrice ?? 0)` gives the engine unit_price 0, and the
//      engine answers with a COMPLETE, VALID result: cost_with_sv 0, unit_sp
//      0, extended_sp 0 -- and gp_pct_snapshot 28, because the GP snapshot is
//      the config value echoed back, not something derived from the price. On
//      screen that is "Rs 0.00" three times beside a confident "28.0%", every
//      one of them wearing the green `live` badge that means "the validated
//      engine computed this".
//
//      The live/est dash CANNOT catch this. It is keyed on `typeof liveValue
//      === "number"` -- on the BACKEND RESULT being absent -- never on the
//      INPUT being unknown. Here the result is present and numerically fine.
//      Verified by reading the resolver out of the deployed bundle, not
//      assumed.
//
//   2. AS A NULL. Letting `undefined` through the multiply yields NaN,
//      JSON.stringify writes NaN as null, and the bridge's _to_decimal
//      rejects None loudly -- but controllers/main.py wraps a line failure as
//      `lines[{index}]: ...` and re-raises, so ONE priceless product aborts
//      the WHOLE batch. Every other line on the quote loses its engine
//      numbers and the user is shown "anixify_cpq.engine_bridge: unit_price
//      is None; the engine needs a number."
//
// Excluding the line dodges both: no null goes to the bridge, no zero goes to
// the engine, and every OTHER line prices exactly as it always did. The
// excluded line's own live columns stay "--" for the honest reason -- it has
// no engine result because it was never priced, which is precisely what the
// dash has always meant.
//
// ⚠️ NOT A SAFETY CONTROL OVER THE NUMBERS -- it is what stops a wrong number
// being produced in the first place. The engine's own refusals stay exactly
// where they are. Compare services/freightBasisGuard.ts, which mirrors an
// engine refusal for UX reasons; this one has no engine counterpart, because
// the engine cannot refuse an input it was never told was missing.
//
// ⚠️ THE PREDICATE IS THE ABSENT netPrice, NOT THE priceStatus FLAG.
//
// priceStatus === "unpriced" is a LABEL -- it drives what the banner and the
// grid say. netPrice === undefined is the FACT, and it is what actually
// decides whether there is anything to send. They are set together when a
// line is picked, but only one of them survives every path: clearing the List
// Price cell in the grid removes the number without anyone having to remember
// to re-set a flag. Keying the guard on the flag would let a line with the
// flag cleared and no price slip through, which is the failure this exists to
// prevent. The flag is derived from the fact, never the reverse.

import type { Material } from "@/types";

/** One line that cannot be priced because nobody has given it a price. */
export interface UnpricedProblem {
  lineNo: number;
  partNo: string;
  description: string;
}

/** Is there a number here for the engine to price from?
 *
 *  The single definition of "priced enough to send". Everything else in this
 *  module and both save seams route through it, so there is one answer to the
 *  question rather than four subtly different truthiness tests.
 *
 *  ⚠️ `!= null`, NEVER a truthy test. A netPrice of 0 that came from a real
 *  listPrice of 0 -- a free-of-charge line, which is a thing that happens --
 *  IS a stated price and must be sent. `!netPrice` would treat it as missing
 *  and silently drop a line the user deliberately zeroed. NaN is excluded
 *  separately: it is never a stated price, and sending it is fork 2 above. */
export function hasPrice(line: Material): boolean {
  return line.netPrice != null && Number.isFinite(line.netPrice);
}

/** Every line on the quote that has no price yet.
 *
 *  Returned in row order, so the banner names them in the order they appear
 *  on screen rather than in whatever order a filter happened to produce. */
export function unpricedProblems(rows: Material[] | undefined): UnpricedProblem[] {
  if (!rows || rows.length === 0) return [];
  return rows
    .filter((r) => !hasPrice(r))
    .map((r) => ({
      lineNo: r.lineNo,
      partNo: r.partNo,
      description: r.description,
    }));
}

/** The lines that may be sent to price_lines: everything with a price.
 *
 *  ⚠️ THE CALLER MUST KEEP THE LINE NUMBERS OF WHAT IT SENT. The endpoint
 *  returns results positionally, so a caller that filters here and then maps
 *  results back against the UNFILTERED row list shifts every result onto the
 *  wrong line the moment one is excluded -- silently, and with real engine
 *  numbers, which is the worst possible way to be wrong. QuoteDetail builds
 *  its `ordered` array from THIS function's output for exactly that reason. */
export function pricableLines(
  rows: Material[] | undefined,
  mode: "domestic" | "import" = "domestic",
): Material[] {
  if (!rows || rows.length === 0) return [];
  return rows.filter((r) => hasPrice(r) && !freightPending(r, mode));
}

/** shipment-price-gap (2026-09-30): an IMPORT line assigned to a shipment
 *  whose freight share has not been allocated yet. The stateless price call
 *  knows no shipments, so pricing it would show freight 0 -- a confident
 *  number the save then changes. It is left OUT of the price request (its
 *  live columns stay "—") and the quote says "freight pending allocation"
 *  until the save allocates the shipment and its share arrives on the row.
 *
 *  ⚠️ `=== undefined`, never a truthy test: an allocated share of 0 is an
 *  answer, not a gap. */
export function freightPending(line: Material, mode: "domestic" | "import"): boolean {
  return mode === "import" && !!line.shipmentKey && line.freightPct === undefined;
}

/** The lines the quote is waiting on the allocator for, in row order. */
export function freightPendingProblems(
  rows: Material[] | undefined,
  mode: "domestic" | "import",
): Array<{ lineNo: number; partNo: string }> {
  if (!rows || mode !== "import") return [];
  return rows
    .filter((r) => freightPending(r, mode))
    .map((r) => ({ lineNo: r.lineNo, partNo: r.partNo }));
}

/** The one sentence the banner says. */
export function freightPendingMessage(
  problems: Array<{ lineNo: number; partNo: string }>,
): string {
  if (problems.length === 0) return "";
  const named = problems.map((p) => `${p.lineNo} (${p.partNo})`).join(", ");
  const many = problems.length !== 1;
  return `Freight pending allocation: line${many ? "s" : ""} ${named} ${many ? "are" : "is"} `
    + "on a shipment that has not been allocated yet, "
    + `so ${many ? "they are" : "it is"} not priced. Save the draft to allocate the shipment's freight, then update prices.`;
}

/** The sentence the banner and both save seams say, built once so they cannot
 *  word the same refusal differently. Mirrors freightBasisMessage's shape.
 *
 *  ⚠️ IT NAMES REMEDIES THAT EXIST -- BOTH OF THEM NOW. The OEM List column
 *  is editable precisely so this message has somewhere to point, and
 *  MaterialTable.unpriced.test.tsx pins that cell as editable for exactly that
 *  reason.
 *
 *  ⚠️ AND THE SECOND REMEDY IS NOT OPTIONAL WORDING. A COMPETITIVE-BID LINE
 *  HAS NO LIST PRICE TO TYPE. The OEM was told it was competing and answered
 *  with a net for that deal only; naming the list price as the sole way out
 *  would push someone into inventing a figure the OEM never quoted, which is
 *  precisely one of the two wrong moves the net-cost affordance was built to
 *  remove. So both cells are named, with the standing-rate one first because
 *  it is the ordinary case. If either stops being editable this sentence
 *  becomes a lie. */
export function unpricedMessage(problems: UnpricedProblem[]): string {
  if (problems.length === 0) return "";

  // Named individually up to three, then counted. A quote can be picked
  // entirely from the real catalog, and listing 180 part numbers in a banner
  // is not a message anyone reads.
  const shown = problems.slice(0, 3).map((p) => `#${p.lineNo} ${p.partNo}`);
  const rest = problems.length - shown.length;

  const subject =
    shown.length === 1
      ? `Line ${shown[0]} has no price`
      : `Lines ${shown.join(", ")}${rest > 0 ? ` and ${rest} more` : ""} have no price`;

  return (
    `${subject}. Products in the catalog carry no price -- every price is ` +
    `computed by the engine from what you enter here -- so a picked line ` +
    `needs a cost before it can be quoted. Enter the OEM List price and its ` +
    `discount, or, on a competitive bid where the OEM quoted a net figure ` +
    `only, type that straight into Net Price. Then press Update prices.`
  );
}
